import { z } from 'zod'
import { mappableChannelSchema } from '../enums/source-channel.enum.js'
import { formatDate } from '../helpers/format.js'
import { sourceKey } from '../helpers/source-key.js'
import { calendarDaySchema } from '../validators/temporal.validator.js'

/**
 * Where a venue's couples come from, saved whole (docs/ad-attribution.spec.md).
 * A listing keeps its `id` when it had one; one left out is removed, with its
 * costs, and any name mapped to it goes back to not mapped. Only mapped names
 * are sent: a name left out isn't mapped.
 */

const listingName = z.string().trim().min(1, 'Give each listing site a name.').max(80, 'That listing site’s name is too long.')

const costInputSchema = z
  .object({
    monthlyCents: z.number().int().min(0, 'A cost can’t be negative.').max(100_000_000, 'That cost is too high.'),
    from: calendarDaySchema,
  })
  .strict()

const listingInputSchema = z
  .object({
    id: z.string().uuid().nullable(),
    name: listingName,
    /** Ignored, and the stored ones kept, for someone who doesn't see ad costs. */
    costs: z.array(costInputSchema).max(60, 'That’s too many costs for one listing site.'),
  })
  .strict()

const mappingInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    channel: mappableChannelSchema,
    /** For a listing, which one: its name in `listings`. */
    listingName: listingName.nullable(),
  })
  .strict()

export const putVenueSourcesInputSchema = z
  .object({
    listings: z.array(listingInputSchema).max(30, 'That’s too many listing sites.'),
    mappings: z.array(mappingInputSchema).max(500, 'That’s too many lead sources.'),
  })
  .strict()
  .superRefine((input, ctx) => {
    const fail = (message: string, path: (string | number)[]): void => {
      ctx.addIssue({ code: 'custom', message, path })
    }
    const listings = new Set<string>()
    const ids = new Set<string>()
    input.listings.forEach((listing, i) => {
      if (listing.id !== null) {
        if (ids.has(listing.id)) fail('A listing site is in the list twice.', ['listings', i, 'id'])
        ids.add(listing.id)
      }
      const key = listing.name.toLowerCase()
      if (listings.has(key)) fail(`Two listing sites are both called “${listing.name}”.`, ['listings', i, 'name'])
      listings.add(key)
      const days = new Set<string>()
      listing.costs.forEach((cost, c) => {
        if (days.has(cost.from)) fail(`${listing.name} has two costs from ${formatDate(cost.from)}.`, ['listings', i, 'costs', c, 'from'])
        days.add(cost.from)
      })
    })
    const names = new Set<string>()
    input.mappings.forEach((mapping, i) => {
      const key = sourceKey(mapping.name)
      if (names.has(key)) fail(`“${mapping.name}” is mapped twice.`, ['mappings', i, 'name'])
      names.add(key)
      const named = mapping.listingName !== null && listings.has(mapping.listingName.toLowerCase())
      if (mapping.channel === 'listing' && !named) fail(`Choose which listing site “${mapping.name}” is.`, ['mappings', i, 'listingName'])
      if (mapping.channel !== 'listing' && mapping.listingName !== null) {
        fail(`Only a listing site names one for “${mapping.name}”.`, ['mappings', i, 'listingName'])
      }
    })
  })

export type PutVenueSourcesInput = z.infer<typeof putVenueSourcesInputSchema>
